import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faDatabase, faHandshake, faHospital, faUserNurse, faTriangleExclamation} from '@fortawesome/free-solid-svg-icons';
import DashboardShell from './DashboardShell';
import {useDashboardData} from './useDashboardData';
import {StatTile} from '../../../components/charts';

type OpsHealthData = {
    database_connected: boolean;
    unapplied_migrations: string[];
    unapplied_migrations_count: number;
    recent_activity: {action: string; target_model: string; description: string; timestamp: string}[];
    counts: {
        active_collaborations: number;
        open_service_requests: number;
        active_clinics: number;
        active_staff: number;
    };
};

/** Platform Operations & Health (P0) - execution_strategy_activity_tracker.md
 * Phase 4.1. The dashboard directly motivated by the production incident
 * found during the original audit (migrations silently out of sync with
 * the live schema) - surfaces that without needing to SSH in. */
const OpsHealthDashboard: React.FC = () => {
    const {data, loading, error} = useDashboardData<OpsHealthData>('/admin-dashboard/dashboards/ops-health/');

    return (
        <DashboardShell
            title="Platform Operations & Health"
            subtitle="System status, pending migrations, and recent admin activity"
            loading={loading}
            error={error}
        >
            {data && (
                <>
                    <div className={`alert ${data.database_connected ? 'alert-success' : 'alert-danger'} d-flex align-items-center gap-2`}>
                        <FontAwesomeIcon icon={faDatabase}/>
                        Database: {data.database_connected ? 'Connected' : 'Unreachable'}
                    </div>

                    {data.unapplied_migrations_count > 0 && (
                        <div className="alert alert-warning d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faTriangleExclamation}/>
                            {data.unapplied_migrations_count} unapplied migration(s): {data.unapplied_migrations.join(', ')}
                        </div>
                    )}

                    <div className="row g-3 mb-4">
                        <div className="col-md-3">
                            <StatTile icon={faHandshake} label="Active Collaborations" value={data.counts.active_collaborations} color="#0ab39c"/>
                        </div>
                        <div className="col-md-3">
                            <StatTile icon={faHandshake} label="Open Service Requests" value={data.counts.open_service_requests} color="#f7b84b"/>
                        </div>
                        <div className="col-md-3">
                            <StatTile icon={faHospital} label="Active Clinics" value={data.counts.active_clinics} color="#405189"/>
                        </div>
                        <div className="col-md-3">
                            <StatTile icon={faUserNurse} label="Active Staff" value={data.counts.active_staff} color="#8b5cf6"/>
                        </div>
                    </div>

                    <div className="uz-panel">
                        <div className="uz-panel-header">
                            <span className="uz-section-title">Recent Admin Activity</span>
                        </div>
                        <div className="uz-panel-body">
                            {data.recent_activity.length === 0 && <p className="text-muted mb-0">No recent activity logged.</p>}
                            {data.recent_activity.map((log, i) => (
                                <div key={i} className="uz-activity-item">
                                    <span className="uz-activity-dot" style={{background: '#0ab39c'}}/>
                                    <div>
                                        <div className="uz-activity-text">{log.action} — {log.target_model}: {log.description}</div>
                                        <div className="uz-activity-time">{new Date(log.timestamp).toLocaleString()}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </DashboardShell>
    );
};

export default OpsHealthDashboard;
